<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-6xl mx-auto px-4">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-6">
        <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-4 group">
          <v-icon small class="group-hover:-translate-x-1 transition-transform" color="inherit">mdi-arrow-left</v-icon>
          <span class="text-[10px] font-black uppercase tracking-widest">Voltar para listagem</span>
        </button>
        <div class="flex items-center justify-between">
            <div>
                <h1 class="text-3xl font-black text-slate-800 uppercase tracking-tight">
                    {{ id ? (isMachinePage ? 'Editar Máquina' : 'Editar Veículo') : (isMachinePage ? 'Nova Máquina' : 'Novo Veículo') }}
                </h1>
                <p class="text-sm text-slate-500 font-medium mt-1">Gestão de frotas e unidades móveis municipais</p>
            </div>
        </div>
      </div>

      <div class="space-y-8 pb-24">
        
        <!-- SEÇÃO 1: IDENTIFICAÇÃO E VÍNCULO -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-file-document-edit-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO E VÍNCULO</h2>
          </div>
          
          <div class="p-8">
            <v-row>
              <v-col cols="12" v-if="isAdmin">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Prefeitura</label>
                <v-autocomplete
                  v-model="data.prefecture_id"
                  :items="prefectures"
                  :readonly="!isAdmin && isPrefecture"
                  item-text="prefecture_social_name"
                  item-value="prefecture_id"
                  placeholder="Selecione a Prefeitura..."
                  solo flat dense hide-details
                  class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main"
                  height="48"
                  @change="selectSecretariats(data.prefecture_id)"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Secretaria</label>
                <v-autocomplete v-model="data.sec_id" :items="secretariats" item-text="sec_name" item-value="sec_id" placeholder="Selecione a Secretaria..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" @change="selectSubSectors(data.sec_id)" />
              </v-col>

              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Subsetor</label>
                <v-autocomplete v-model="data.subsec_id" :items="subsectors" item-text="subsec_desc" item-value="subsec_id" placeholder="Selecione o Subsetor..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Placa</label>
                <v-text-field v-model="data.vehicle_board" placeholder="EX: ABC-1234" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold uppercase" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Número do Renavam</label>
                <v-text-field v-model="data.vehicle_renavan" placeholder="00000000000" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Unidade Gestora</label>
                <v-text-field v-model="data.vehicle_cod" placeholder="000000" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Cod. Maquina</label>
                <v-text-field v-model="data.vehicle_cod_maquina" placeholder="000000" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Marca</label>
                <v-autocomplete v-model="data.brand_id" :items="brands" :item-text="getBrandText" item-value="brand_id" placeholder="Selecione a Marca..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Vínculo</label>
                <v-autocomplete v-model="data.bond_id" :items="bonds" item-text="bond_desc" item-value="bond_id" placeholder="Selecione..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Atendimento</label>
                <v-autocomplete v-model="data.attendance_id" :items="attendances" item-text="attendance_desc" item-value="attendance_id" placeholder="Selecione..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Proprietário</label>
                <v-autocomplete v-model="data.vehicle_proprietario" :items="proprietario" item-text="name" item-value="id" placeholder="Selecione..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Locador</label>
                <v-autocomplete v-model="data.vehicle_locador" :items="locador" item-text="name" item-value="id" placeholder="Selecione..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Categoria</label>
                <v-select v-model="data.vehicle_categoria" :items="[{ text: 'Selecione...', value: null }, { text: 'VEÍCULO DE PASSEIO / CARGA', value: 1 }, { text: 'MÁQUINA PESADA / TRATOR', value: 2 }]" item-text="text" item-value="value" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold" height="48" />
              </v-col>
            </v-row>
          </div>
        </section>

        <!-- SEÇÃO 2: ESPECIFICAÇÕES E CONSUMO -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-engine-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ESPECIFICAÇÕES E CONSUMO</h2>
          </div>
          
          <div class="p-8">
            <v-row>
              <v-col cols="12" md="3">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Ano Fab.</label>
                <v-text-field v-model="data.vehicle_manufacturing_year" v-mask="'####'" placeholder="2022" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>
              <v-col cols="12" md="3">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Ano Modelo</label>
                <v-text-field v-model="data.vehicle_model_year" v-mask="'####'" placeholder="2023" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Modelo</label>
                <v-autocomplete v-model="data.model_id" :items="models" :item-text="getModelText" item-value="model_id" placeholder="Selecione o Modelo..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold" height="48" />
              </v-col>

              <v-col cols="12" md="3">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Cor</label>
                <v-autocomplete v-model="data.color_id" :items="colors" item-text="color_desc" item-value="color_id" placeholder="Selecione a Cor..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>
              <v-col cols="12" md="3">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Combustível</label>
                <v-autocomplete v-model="data.fuel_id" :items="fuels" item-text="fuel_desc" item-value="fuel_id" placeholder="Selecione..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>
              <v-col cols="12" md="3">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Consumo</label>
                <v-autocomplete v-model="data.consumption_id" :items="consumption" item-text="consumption_desc" item-value="consumption_id" placeholder="Selecione..." solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>
              <v-col cols="12" md="3">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Tanque (L)</label>
                <v-text-field v-model="data.vehicle_tank_capacity" v-mask-decimal.br="2" placeholder="0,00" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Consumo Mín. (Km/L)</label>
                <v-text-field v-model="data.vehicle_average_consumption_minimum" v-mask-decimal.br="2" placeholder="0,00" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold" height="48" />
              </v-col>
              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Consumo Máx. (Km/L)</label>
                <v-text-field v-model="data.vehicle_average_consumption_maximum" v-mask-decimal.br="2" placeholder="0,00" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold" height="48" />
              </v-col>
              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Limite (Litros)</label>
                <v-text-field v-model="data.vehicle_consumption_liters_maximum" v-mask-decimal.br="2" placeholder="0,00" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Consumo Hora</label>
                <v-text-field v-model="data.vehicle_consumption_liters_hour" v-mask-decimal.br="2" placeholder="0,00" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold" height="48" />
              </v-col>
              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Valor Atual</label>
                <v-text-field v-model="data.vehicle_current_value" v-mask-decimal.br="2" placeholder="0,00" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main font-bold" height="48" />
              </v-col>
              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Aquisição</label>
                <v-text-field v-model="data.vehicle_date_acquisition" v-mask-date.br placeholder="DD/MM/AAAA" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>

              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Encerramento</label>
                <v-text-field v-model="data.vehicle_date_closure" v-mask-date.br placeholder="DD/MM/AAAA" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>
              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Últ. Reg. TCE</label>
                <v-text-field v-model="data.vehicle_date_last_tce_record" v-mask-date.br placeholder="DD/MM/AAAA" solo flat dense hide-details class="bg-slate-50 border border-slate-100 rounded-lg custom-select-main" height="48" />
              </v-col>
              <v-col cols="12" md="4">
                <div class="p-4 bg-emerald-50 rounded-xl border border-emerald-100 relative">
                  <label class="block text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1">KM do Veículo</label>
                  <v-text-field v-model="data.vehicle_km" v-mask-decimal.br="2" placeholder="0,00" solo flat dense hide-details class="bg-transparent font-black text-xl text-emerald-700" height="40" />
                  
                  <!-- Hodômetro Quebrado Option -->
                  <div class="mt-2 flex items-center gap-2 cursor-pointer" @click="data.vehicle_broken_odometer = !data.vehicle_broken_odometer">
                    <v-icon small :color="data.vehicle_broken_odometer ? 'red' : 'slate-400'">
                      {{ data.vehicle_broken_odometer ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                    </v-icon>
                    <span class="text-[10px] font-bold uppercase tracking-tighter" :class="data.vehicle_broken_odometer ? 'text-red-600' : 'text-slate-500'">
                      Hodômetro Quebrado
                    </span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </section>

        <!-- SEÇÃO 3: PARÂMETROS E OBSERVAÇÕES -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-tune-variant</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">PARÂMETROS E OBSERVAÇÕES</h2>
          </div>
          
          <div class="p-8">
            <v-row>
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-4">Parâmetros do Veículo</label>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div v-for="param in [
                    { key: 'vehicle_reports', label: 'Relatórios', icon: 'mdi-file-chart' },
                    { key: 'vehicle_hodometro', label: 'Hodômetro', icon: 'mdi-speedometer' },
                    { key: 'vehicle_active', label: 'Veículo Ativo', icon: 'mdi-power' },
                    { key: 'abastecimento_extra', label: 'Abastec. Extra', icon: 'mdi-fuel-plus' },
                    { key: 'abastecimento_autorizacao', label: 'Abastec. Autorização', icon: 'mdi-shield-check' },
                    { key: 'vehicle_horimetro', label: 'Horímetro', icon: 'mdi-clock-outline' }
                  ]" :key="param.key"
                  class="flex items-center gap-3 p-3 bg-slate-50 border border-slate-100 rounded-lg hover:border-slate-200 transition-all cursor-pointer"
                  @click="toggleParam(param.key)">
                    <div class="w-12 h-8 bg-white border border-slate-200 rounded flex items-center justify-center">
                       <v-icon small :color="!!data[param.key] ? 'emerald' : 'slate-300'">{{ !!data[param.key] ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                    </div>
                    <span class="text-[10px] font-black uppercase text-slate-500 tracking-tight">{{ param.label }}</span>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-4">Observações Adicionais</label>
                <div class="bg-slate-50 p-4 rounded-xl border border-slate-100 h-full min-h-[160px]">
                  <v-textarea v-model="data.vehicle_observations" placeholder="Notas importantes sobre este veículo..." solo flat dense hide-details rows="6" class="bg-transparent text-sm font-medium" auto-grow />
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- FOOTER AÇÕES -->
          <div class="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end items-center gap-4" style="border-radius: 0 0 12px 12px;">
            <button
              type="button"
              @click="$router.back()"
              style="background: none; border: none; cursor: pointer; white-space: nowrap;"
              class="text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest transition-colors"
            >
              CANCELAR
            </button>
            <button
              type="button"
              @click="validate"
              :disabled="loader"
              style="background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 8px; border-radius: 8px; padding: 0 24px; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; border: none; cursor: pointer; height: 42px; width: auto; flex-shrink: 0;"
            >
              <v-icon v-if="loader" small class="animate-spin" color="white">mdi-loading</v-icon>
              <v-icon v-else small color="white">mdi-check</v-icon>
              <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
            </button>
          </div>
        </section>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    prefectures: [],
    secretariats: [],
    subsectors: [],
    brands: [],
    models: [],
    colors: [],
    fuels: [],
    consumption: [],
    billings: ["POR VEICULO", "POR SECRETARIA"],
    bonds: [],
    attendances: [],
    proprietario: [],
    locador: [],
    snack: { show: false, message: "", color: "success" },
    data: {
      vehicle_id: null,
      prefecture_id: null,
      sec_id: null,
      subsec_id: null,
      model_id: null,
      brand_id: null,
      color_id: null,
      fuel_id: null,
      consumption_id: null,
      bond_id: null,
      billing_type: "",
      attendance_id: null,
      vehicle_board: null,
      vehicle_manufacturing_year: null,
      vehicle_model_year: null,
      vehicle_tank_capacity: 0.0,
      vehicle_average_consumption_minimum: 0.0,
      vehicle_average_consumption_maximum: 0.0,
      vehicle_consumption_liters_maximum: 0.0,
      vehicle_consumption_liters_hour: 0.0,
      vehicle_number_of_liters: 0.0,
      vehicle_amount_value: 0.0,
      vehicle_date_acquisition: null,
      vehicle_date_closure: null,
      vehicle_date_last_tce_record: null,
      vehicle_last_kmh: 0.0,
      vehicle_km: 0.0,
      vehicle_current_value: 0.0,
      vehicle_observations: null,
      vehicle_reports: null,
      vehicle_hodometro: null,
      vehicle_horimetro: null,
      vehicle_active: null,
      vehicle_renavan: "",
      vehicle_proprietario: null,
      vehicle_locador: null,
      vehicle_cod: null,
      vehicle_cod_maquina: null,
      vehicle_categoria: null,
      abastecimento_autorizacao: false,
      abastecimento_extra: false,
      vehicle_broken_odometer: false,
    },
    prefecture_id: null,
  }),
  computed: {
    isAdmin() {
      const user = this.$store.getters.getUserData;
      return user && user.group_id == 1;
    },
    isPrefecture() {
      const user = this.$store.getters.getUserData;
      return user && user.table_key === 'prefectures';
    },
    isMachinePage() {
      return this.$route.path.includes('machines-manager');
    },
  },
  created() {
    console.log("DEBUG - User Data:", this.$store.getters.getUserData);
    console.log("DEBUG - Is Admin:", this.isAdmin);
    console.log("DEBUG - Is Prefecture:", this.isPrefecture);

    this.$http.get("/prefectures").then((r) => {
      console.log("DEBUG - Prefectures loaded:", r.data.length, "items");
      this.prefectures = r.data;
    }).catch(e => {
      console.error("DEBUG - Error loading prefectures:", e);
    });
    this.$http.get("/proprietario").then((r) => (this.proprietario = r.data));
    this.$http.get("/locador").then((r) => (this.locador = r.data));
    this.$http.get("/vehicles/brands").then((r) => (this.brands = r.data.filter((i) => i.brand_num !== null && i.brand_num !== "")));
    this.$http.get("/vehicles/models").then((r) => (this.models = r.data.filter((i) => i.model_num !== null && i.model_num !== "")));
    this.$http.get("/vehicles/colors").then((r) => (this.colors = r.data));
    this.$http.get("/settings/fuels").then((r) => (this.fuels = r.data));
    this.$http.get("/settings/bonds").then((r) => (this.bonds = r.data));
    this.$http.get("/settings/consumption").then((r) => (this.consumption = r.data));
    this.$http.get("/settings/attendance_types").then((r) => (this.attendances = r.data));
    
    if (this.isPrefecture && !this.isAdmin) {
      this.data.prefecture_id = this.$store.getters.getUserData.table_id;
      this.selectSecretariats(this.data.prefecture_id);
    }

    if (!this.id && this.isMachinePage) {
      this.data.vehicle_horimetro = true;
      this.data.vehicle_hodometro = false;
    }

    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/vehicles/${this.id}`).then((r) => {
        this.prefecture_id = r.data[0].prefecture_id;
        this.data = r.data[0];
        
        // Converter campos booleanos que podem vir como 1/0 do banco
        const booleanFields = [
          'vehicle_hodometro', 
          'vehicle_horimetro', 
          'vehicle_active', 
          'vehicle_reports', 
          'abastecimento_extra', 
          'abastecimento_autorizacao',
          'vehicle_broken_odometer'
        ];
        
        booleanFields.forEach(field => {
          this.data[field] = !!this.data[field];
        });

        // Garantir exclusividade entre hodômetro e horímetro no carregamento
        if (this.data.vehicle_hodometro) this.data.vehicle_horimetro = false;
        else if (this.data.vehicle_horimetro) this.data.vehicle_hodometro = false;

        const decimalFields = [
          'vehicle_average_consumption_maximum',
          'vehicle_average_consumption_minimum',
          'vehicle_consumption_liters_hour',
          'vehicle_consumption_liters_maximum',
          'vehicle_tank_capacity',
          'vehicle_current_value',
          'vehicle_amount_value',
          'vehicle_number_of_liters',
          'vehicle_km'
        ];

        decimalFields.forEach(field => {
          if (r.data[0][field] !== null && r.data[0][field] !== undefined) {
             this.data[field] = Number(r.data[0][field]).toFixed(2);
          }
        });

        this.data.vehicle_date_acquisition = this.data.vehicle_date_acquisition && this.convertDate(r.data[0].vehicle_date_acquisition);
        this.data.vehicle_date_closure = this.data.vehicle_date_closure && this.convertDate(r.data[0].vehicle_date_closure);
        this.data.vehicle_date_last_tce_record = this.data.vehicle_date_last_tce_record && this.convertDate(r.data[0].vehicle_date_last_tce_record);

        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
        this.selectSecretariats(this.data.prefecture_id);
        this.selectSubSectors(this.data.sec_id);
      });
    }
  },
  methods: {
    validate() {
      if (!this.data.prefecture_id || !this.data.sec_id) {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atenção: Preencha os campos obrigatórios!";
        return;
      }
      this.createOrUpdate();
    },
    selectSecretariats(id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/prefectures/secretariats/${id}`).then((r) => {
        this.secretariats = r.data;
        this.$store.dispatch("setLoader", false);
      });
    },
    selectSubSectors(id) {
      this.$http.get(`/prefectures/subsectors/${id}`).then((r) => {
        this.subsectors = r.data;
      });
    },
    convertDate(v) {
      if (v) {
        v = v.replace("-", "").replace("-", "");
        let year = v.substring(0, 4);
        let month = v.substring(4, 6);
        let day = v.substring(6, 8);
        return `${day}/${month}/${year}`;
      }
      return false;
    },
    createOrUpdate() {
      this.loader = true;
      const method = this.id ? 'put' : 'post';
      const url = this.id ? `/vehicles/${this.id}` : '/vehicles';
      
      this.$store.dispatch("setLoader", this.loader);
      this.$http[method](url, this.data)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result || "Sucesso!";
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
          setTimeout(() => {
            const basePath = this.isMachinePage ? "/machines-manager" : "/vehicles-manager";
            this.$router.push(this.id ? `${basePath}/${this.prefecture_id}` : basePath);
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    getModelText(item) {
      return `${item.model_desc} - ${item.model_num}`;
    },
    getBrandText(item) {
      return `${item.brand_desc} - ${item.brand_num}`;
    },
    safeToFixed(value, digits = 2) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return Number(0).toFixed(digits);
      return numericValue.toFixed(digits);
    },
    toggleParam(key) {
      this.data[key] = !this.data[key];
      // Lógica de exclusividade entre hodômetro e horímetro
      if (key === 'vehicle_hodometro' && this.data[key]) {
        this.data.vehicle_horimetro = false;
      } else if (key === 'vehicle_horimetro' && this.data[key]) {
        this.data.vehicle_hodometro = false;
      }
    }
  },
};
</script>

<style scoped>
.sci-page-wrapper {
  background-color: #f8fafc;
  min-height: 100vh;
  padding: 80px 20px;
}

.custom-select-main >>> .v-input__control .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 16px !important;
}

.custom-select-main >>> input,
.custom-select-main >>> .v-select__selection {
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.8rem;
  color: #334155 !important;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 12px !important;
}
</style>
